import { gql } from '@apollo/client'
import { useApolloClient, useFragment, useQuery } from '@apollo/client/react'
import { useCallback } from 'react'
import {
  type BasicUserFieldsFragment,
  BasicUserFieldsFragmentDoc,
  GetCurrentUserDocument,
  UpdateUserBasicDocument,
} from '../gql/graphql'

type UpdateUserFields = {
  name?: string
  avatar?: string
  phoneCode?: string
}

gql`
  fragment BasicUserFields on User {
    id
    name
    avatar
    openid
    mobile
    gender
    isAppAdmin
  }

  query GetCurrentUser {
    currentUser @client {
       ...BasicUserFields
    }
  }

  mutation UpdateUserBasic($userProfile: UpdateUserProfileInput!) {
    updateUserProfile(userProfile: $userProfile) {
      ...BasicUserFields
    }
  }
`

/**
 * @returns The current logged-in user from the Apollo cache and a function to update
 * the user.
 *
 * The user is written to the cache when the app starts during the `login` mutation
 * using `client.writeQuery` for latter retrieval by the local query `currentUser`.
 *
 * The hook could return undefined as the currentUser, since before the `login` mutation
 * is executed, we don not know even the openid of the user.
 */
export default function useCurrentUser(): [
  BasicUserFieldsFragment | undefined,
  (userUpdates: UpdateUserFields) => void,
] {
  const client = useApolloClient()
  const { data } = useQuery(GetCurrentUserDocument)
  const { data: currentUser } = useFragment({
    fragment: BasicUserFieldsFragmentDoc,
    from: data?.currentUser ?? null,
  })

  /**
   * Note with `updateCurrentUser` we are only updating a limit subset of fields
   * defined by `UpdateUserFields`, and the return type of the mutation is `UserBasic`
   */
  const updateCurrentUser = useCallback(
    async (userUpdates: UpdateUserFields) => {
      if (currentUser?.openid) {
        await client.mutate({
          mutation: UpdateUserBasicDocument,
          variables: {
            userProfile: userUpdates,
          },
        })
      }
    },
    [client, currentUser?.openid],
  )

  return [currentUser as BasicUserFieldsFragment, updateCurrentUser]
}
